import * as types from "./types";
import { get, put, post } from "@zendesk/knowledge-http-client";
import { getCurrentBrandId, getCSRFToken } from "@zendesk/knowledge-context";
import t from "@zendesk/client-i18n-tools";
import {
  showSuccess,
  showError,
} from "@zendesk/knowledge-redux/lib/actions/NotificationActions";
import {
  getItemAbove,
  getItemBelow,
  isMovingDown,
} from "../../utils/positioning";

function fetchSectionsRequest() {
  return {
    type: types.FETCH_SECTION_SECTIONS_REQUEST,
  };
}

function fetchSectionsSuccess(sections, page, pageCount, count) {
  return {
    type: types.FETCH_SECTION_SECTIONS_SUCCESS,
    sections,
    page,
    pageCount,
    count,
  };
}

function fetchSectionsFailure(error) {
  return {
    type: types.FETCH_SECTION_SECTIONS_FAILURE,
    error: true,
    payload: error,
  };
}

export function fetchSections(sectionId, { page }) {
  return (dispatch, getState) => {
    dispatch(fetchSectionsRequest());

    return get(
      `/hc/api/internal/arrange_articles/sections/${sectionId}/sections?multibrand=true&brand_id=${getCurrentBrandId()}&page=${page}&per_page=5`
    )
      .then(({ sections, page, page_count, count }) =>
        dispatch(fetchSectionsSuccess(sections, page, page_count, count))
      )
      .catch(err => {
        dispatch(fetchSectionsFailure(err));
      });
  };
}

function fetchArticlesRequest() {
  return {
    type: types.FETCH_SECTION_ARTICLES_REQUEST,
  };
}

function fetchArticlesSuccess(articles, page, pageCount, count) {
  return {
    type: types.FETCH_SECTION_ARTICLES_SUCCESS,
    articles,
    page,
    pageCount,
    count,
  };
}

function fetchArticlesFailure(error) {
  return {
    type: types.FETCH_SECTION_ARTICLES_FAILURE,
    error: true,
    payload: error,
  };
}

export function fetchArticles(sectionId, { page }) {
  return (dispatch, getState) => {
    dispatch(fetchArticlesRequest());

    return get(
      `/hc/api/internal/arrange_articles/sections/${sectionId}/articles?multibrand=true&brand_id=${getCurrentBrandId()}&page=${page}&per_page=30`
    )
      .then(({ articles, page, page_count, count }) =>
        dispatch(fetchArticlesSuccess(articles, page, page_count, count))
      )
      .catch(err => {
        dispatch(fetchArticlesFailure(err));
      });
  };
}

function fetchSectionRequest() {
  return {
    type: types.FETCH_SECTION_REQUEST,
  };
}

function fetchSectionSuccess(section) {
  return {
    type: types.FETCH_SECTION_SUCCESS,
    section,
  };
}

function fetchSectionFailure(error) {
  return {
    type: types.FETCH_SECTION_FAILURE,
    error: true,
    payload: error,
  };
}

export function fetchSection(id, sectionPage, articlePage) {
  return (dispatch, getState) => {
    dispatch(fetchSectionRequest());

    return get(
      `/hc/api/internal/arrange_articles/sections/${id}?multibrand=true&brand_id=${getCurrentBrandId()}`
    )
      .then(({ section }) => {
        dispatch(fetchSections(section.id, { page: sectionPage }));
        dispatch(fetchArticles(section.id, { page: articlePage }));
        dispatch(fetchSectionSuccess(section));
      })
      .catch(err => {
        dispatch(fetchSectionFailure(err));
      });
  };
}

function moveArticleToSectionRequest(
  movedArticleId,
  destinationSectionId,
  sourceIndex
) {
  return {
    type: types.MOVE_ARTICLE_TO_SECTION_REQUEST,
    movedArticleId,
    destinationSectionId,
    sourceIndex,
  };
}

function moveArticleToSectionSuccess(articleId, destinationSectionId) {
  return {
    type: types.MOVE_ARTICLE_TO_SECTION_SUCCESS,
    articleId,
    destinationSectionId,
  };
}

function moveArticleToSectionFailure(
  movedItem,
  destinationSectionId,
  sourceIndex,
  error
) {
  return {
    type: types.MOVE_ARTICLE_TO_SECTION_FAILURE,
    error: true,
    payload: error,
    movedItem,
    destinationSectionId,
    sourceIndex,
  };
}

export function moveArticleToSection(articleId, sectionId, sourceIndex) {
  return (dispatch, getState) => {
    const {
      articles: { list },
    } = getState();

    const movedItem = list.find(({ id }) => articleId === id);

    dispatch(moveArticleToSectionRequest(articleId, sectionId, sourceIndex));

    // Replace with multibranded internal api endpoint when it's ready
    return put(
      `/hc/api/internal/arrange_articles/articles/${articleId}?multibrand=true&brand_id=${getCurrentBrandId()}`,
      {
        article: {
          section_id: sectionId,
        },
      },
      {
        csrfToken: getCSRFToken(),
      }
    )
      .then(() => {
        dispatch(moveArticleToSectionSuccess(articleId, sectionId));
        dispatch(
          showSuccess(t("knowledge-arrange.notification.articleMoved.success"))
        );
      })
      .catch(err => {
        dispatch(
          moveArticleToSectionFailure(movedItem, sectionId, sourceIndex, err)
        );
        dispatch(
          showError(t("knowledge-arrange.notification.articleMoved.failure"))
        );
      });
  };
}

function positionArticleRequest(sourceIndex, destinationIndex) {
  return {
    type: types.POSITION_ARTICLE_REQUEST,
    sourceIndex,
    destinationIndex,
  };
}

function positionArticleSuccess(sourceIndex, destinationIndex) {
  return {
    type: types.POSITION_ARTICLE_SUCCESS,
    sourceIndex,
    destinationIndex,
  };
}

function positionArticleFailure(sourceIndex, destinationIndex, error) {
  return {
    type: types.POSITION_ARTICLE_FAILURE,
    error: true,
    payload: error,
    sourceIndex,
    destinationIndex,
  };
}

export function positionArticle(
  sectionId,
  articleId,
  sourceIndex,
  destinationIndex
) {
  return (dispatch, getState) => {
    const {
      articles: { list },
    } = getState();

    let payload = {
      positioned_article_id: articleId,
    };

    if (isMovingDown(sourceIndex, destinationIndex)) {
      const item = getItemAbove(list, sourceIndex, destinationIndex);
      payload.below_id = item && item.id;
    } else {
      const item = getItemBelow(list, sourceIndex, destinationIndex);
      payload.above_id = item && item.id;
    }

    dispatch(positionArticleRequest(sourceIndex, destinationIndex));

    return post(
      `/hc/api/internal/arrange_articles/sections/${sectionId}/position_article?multibrand=true&brand_id=${getCurrentBrandId()}`,
      payload,
      {
        csrfToken: getCSRFToken(),
      }
    )
      .then(() => {
        dispatch(positionArticleSuccess(sourceIndex, destinationIndex));
      })
      .catch(err => {
        dispatch(positionArticleFailure(sourceIndex, destinationIndex, err));
        dispatch(
          showError(
            t("knowledge-arrange.notification.articlePositioned.failure")
          )
        );
      });
  };
}

function positionSectionRequest(sourceIndex, destinationIndex) {
  return {
    type: types.POSITION_SECTION_REQUEST,
    sourceIndex,
    destinationIndex,
  };
}

function positionSectionSuccess(sourceIndex, destinationIndex) {
  return {
    type: types.POSITION_SECTION_SUCCESS,
    sourceIndex,
    destinationIndex,
  };
}

function positionSectionFailure(sourceIndex, destinationIndex, error) {
  return {
    type: types.POSITION_SECTION_FAILURE,
    error: true,
    payload: error,
    sourceIndex,
    destinationIndex,
  };
}

export function positionSection(
  sectionId,
  positionedSectionId,
  sourceIndex,
  destinationIndex
) {
  return (dispatch, getState) => {
    const {
      sections: { list },
    } = getState();

    let payload = {
      positioned_section_id: positionedSectionId,
    };

    if (isMovingDown(sourceIndex, destinationIndex)) {
      const item = getItemAbove(list, sourceIndex, destinationIndex);
      payload.below_id = item && item.id;
    } else {
      const item = getItemBelow(list, sourceIndex, destinationIndex);
      payload.above_id = item && item.id;
    }

    dispatch(positionSectionRequest(sourceIndex, destinationIndex));

    return post(
      `/hc/api/internal/arrange_articles/sections/${sectionId}/position_section?multibrand=true&brand_id=${getCurrentBrandId()}`,
      payload,
      {
        csrfToken: getCSRFToken(),
      }
    )
      .then(() => {
        dispatch(positionSectionSuccess(sourceIndex, destinationIndex));
      })
      .catch(err => {
        dispatch(positionSectionFailure(sourceIndex, destinationIndex, err));
        dispatch(
          showError(
            t("knowledge-arrange.notification.sectionPositioned.failure")
          )
        );
      });
  };
}

function moveSectionToSectionRequest(movedSectionId, destinationSectionId) {
  return {
    type: types.MOVE_SECTION_TO_SECTION_REQUEST,
    movedSectionId,
    destinationSectionId,
  };
}

function moveSectionToSectionSuccess(destinationSectionId) {
  return {
    type: types.MOVE_SECTION_TO_SECTION_SUCCESS,
    destinationSectionId,
  };
}

function moveSectionToSectionFailure(
  item,
  destinationSectionId,
  sourceIndex,
  error
) {
  return {
    type: types.MOVE_SECTION_TO_SECTION_FAILURE,
    error: true,
    payload: error,
    item,
    sourceIndex,
    destinationSectionId,
  };
}

export function moveSectionToSection(
  movedSectionId,
  destinationSectionId,
  sourceIndex
) {
  return (dispatch, getState) => {
    const {
      sections: { list },
    } = getState();

    const movedItem = list.find(({ id }) => movedSectionId === id);

    dispatch(moveSectionToSectionRequest(movedSectionId, destinationSectionId));

    return put(
      `/hc/api/internal/arrange_articles/sections/${movedSectionId}?multibrand=true&brand_id=${getCurrentBrandId()}`,
      {
        section: {
          parent_section_id: destinationSectionId,
        },
      },
      {
        csrfToken: getCSRFToken(),
      }
    )
      .then(() => {
        dispatch(moveSectionToSectionSuccess(destinationSectionId));
        dispatch(
          showSuccess(t("knowledge-arrange.notification.sectionMoved.success"))
        );
      })
      .catch(err => {
        dispatch(
          moveSectionToSectionFailure(
            movedItem,
            destinationSectionId,
            sourceIndex,
            err
          )
        );

        dispatch(
          showError(t("knowledge-arrange.notification.sectionMoved.failure"))
        );
      });
  };
}

function moveSectionToCategoryRequest(movedSectionId) {
  return {
    type: types.MOVE_SECTION_TO_CATEGORY_REQUEST,
    movedSectionId,
  };
}

function moveSectionToCategorySuccess() {
  return {
    type: types.MOVE_SECTION_TO_CATEGORY_SUCCESS,
  };
}

function moveSectionToCategoryFailure(error) {
  return {
    type: types.MOVE_SECTION_TO_CATEGORY_FAILURE,
    error: true,
    payload: error,
  };
}

export function moveSectionToCategory(
  movedSectionId,
  destinationCategoryId,
  sourceIndex
) {
  return (dispatch, getState) => {
    dispatch(moveSectionToCategoryRequest(movedSectionId));

    return put(
      `/hc/api/internal/arrange_articles/sections/${movedSectionId}?multibrand=true&brand_id=${getCurrentBrandId()}`,
      {
        section: {
          category_id: destinationCategoryId,
        },
      },
      {
        csrfToken: getCSRFToken(),
      }
    )
      .then(() => {
        dispatch(moveSectionToCategorySuccess());
        dispatch(
          showSuccess(t("knowledge-arrange.notification.sectionMoved.success"))
        );
      })
      .catch(err => {
        dispatch(moveSectionToCategoryFailure(err));
        dispatch(
          showError(t("knowledge-arrange.notification.sectionMoved.failure"))
        );
      });
  };
}

function moveSectionToTopRequest(sourceIndex) {
  return {
    type: types.MOVE_SECTION_TO_TOP_REQUEST,
    sourceIndex,
  };
}

function moveSectionToTopSuccess() {
  return {
    type: types.MOVE_SECTION_TO_TOP_SUCCESS,
  };
}

function moveSectionToTopFailure(sourceIndex, item, error) {
  return {
    type: types.MOVE_SECTION_TO_TOP_FAILURE,
    error: true,
    payload: error,
    sourceIndex,
    item,
  };
}

export function moveSectionToTop(movedSection, sourceIndex) {
  return (dispatch, getState) => {
    const { section } = getState();

    dispatch(moveSectionToTopRequest(sourceIndex));

    return post(
      `/hc/api/internal/arrange_articles/sections/${
        section.id
      }/position_section?multibrand=true&brand_id=${getCurrentBrandId()}`,
      {
        positioned_section_id: movedSection.id,
        below_id: null,
      },
      {
        csrfToken: getCSRFToken(),
      }
    )
      .then(() => {
        dispatch(moveSectionToTopSuccess());
      })
      .catch(err => {
        dispatch(moveSectionToTopFailure(sourceIndex, movedSection, err));
        dispatch(
          showError(
            t("knowledge-arrange.notification.sectionPositioned.failure")
          )
        );
      });
  };
}

function moveArticleToTopRequest(sourceIndex) {
  return {
    type: types.MOVE_ARTICLE_TO_TOP_REQUEST,
    sourceIndex,
  };
}

function moveArticleToTopSuccess() {
  return {
    type: types.MOVE_ARTICLE_TO_TOP_SUCCESS,
  };
}

function moveArticleToTopFailure(sourceIndex, item, error) {
  return {
    type: types.MOVE_ARTICLE_TO_TOP_FAILURE,
    error: true,
    payload: error,
    sourceIndex,
    item,
  };
}

export function moveArticleToTop(movedArticle, sourceIndex) {
  return (dispatch, getState) => {
    const { section } = getState();

    dispatch(moveArticleToTopRequest(sourceIndex));

    return post(
      `/hc/api/internal/arrange_articles/sections/${
        section.id
      }/position_article?multibrand=true&brand_id=${getCurrentBrandId()}`,
      {
        positioned_article_id: movedArticle.id,
        below_id: null,
      },
      {
        csrfToken: getCSRFToken(),
      }
    )
      .then(() => {
        dispatch(moveArticleToTopSuccess());
      })
      .catch(err => {
        dispatch(moveArticleToTopFailure(sourceIndex, movedArticle, err));
        dispatch(
          showError(
            t("knowledge-arrange.notification.articlePositioned.failure")
          )
        );
      });
  };
}



// WEBPACK FOOTER //
// ./src/actions/section/index.js